import React, { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Phone, ShieldCheck, Save, Send, Trash2, Copy, ExternalLink, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const randomToken = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(18)))
    .map((b) => b.toString(36).padStart(2, '0'))
    .join('')
    .slice(0, 32);

export const WhatsAppCloudDialog = ({ campaign, open, onOpenChange }) => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ phone_number_id: '', access_token: '', verify_token: '', display_phone: '', waba_id: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');

  const webhookUrl = campaign ? `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/whatsapp/${campaign.id}` : '';

  const load = useCallback(async () => {
    if (!campaign) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/campaigns/${campaign.id}/whatsapp-cloud`);
      setCfg(res.data);
      setForm({
        phone_number_id: res.data.phone_number_id || '',
        access_token: '',
        verify_token: '',
        display_phone: res.data.display_phone || '',
        waba_id: res.data.waba_id || '',
      });
    } catch {
      toast.error('No se pudo cargar la configuración');
    } finally {
      setLoading(false);
    }
  }, [campaign]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const handleSave = async () => {
    if (!form.phone_number_id || !form.access_token || !form.verify_token) {
      toast.error('Phone Number ID, Access Token y Verify Token son obligatorios');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/campaigns/${campaign.id}/whatsapp-cloud`, form);
      toast.success(`WhatsApp Cloud conectado a ${campaign.name}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testTo) { toast.error('Escribe un número en formato E.164, ej: 593991234567'); return; }
    try {
      const res = await axios.post(`${API}/campaigns/${campaign.id}/whatsapp-cloud/test`, { to: testTo });
      res.data.sent ? toast.success(res.data.message) : toast.error(res.data.message);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error en la prueba');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/campaigns/${campaign.id}/whatsapp-cloud`);
      toast.success('Configuración eliminada');
      await load();
    } catch { toast.error('Error al eliminar'); }
  };

  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copiado`); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="wa-cloud-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-emerald-700 dark:text-emerald-400" />
            WhatsApp Business Cloud · {campaign?.name}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-5">
            <div className="flex items-center gap-2" data-testid="wa-cloud-status">
              {cfg?.connected ? (
                <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 gap-1">
                  <CheckCircle2 className="w-3 h-3" /> {cfg.status === 'live' ? 'En vivo' : 'Credenciales guardadas'}
                </Badge>
              ) : (
                <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30 gap-1">
                  <AlertTriangle className="w-3 h-3" /> Sin configurar
                </Badge>
              )}
              {cfg?.display_phone && <span className="text-xs text-muted-foreground">{cfg.display_phone}</span>}
            </div>

            <div className="p-3 rounded-xl bg-muted/30 border border-border/60 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1 · Webhook para Meta</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[11px] break-all bg-background/70 rounded-lg px-2 py-1.5">{webhookUrl}</code>
                <Button size="icon" variant="ghost" onClick={() => copy(webhookUrl, 'Webhook')} data-testid="wa-copy-webhook">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-xs text-brand-deep dark:text-brand-deep dark:text-brand-green hover:underline" data-testid="wa-meta-link">
                Abrir Meta for Developers <ExternalLink className="w-3 h-3" />
              </a>
              <p className="text-[11px] text-muted-foreground">
                Pega esta URL en WhatsApp → Configuration → Callback URL, usa tu Verify Token y suscríbete al campo <b>messages</b>.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">2 · Credenciales de esta campaña</p>
              <div>
                <Label className="text-xs">Phone Number ID</Label>
                <Input data-testid="wa-phone-number-id" value={form.phone_number_id} className="mt-1"
                  placeholder="123456789012345"
                  onChange={(e) => setForm({ ...form, phone_number_id: e.target.value.trim() })} />
              </div>
              <div>
                <Label className="text-xs">Número visible (opcional)</Label>
                <Input data-testid="wa-display-phone" value={form.display_phone} className="mt-1"
                  placeholder="+593 99 123 4567"
                  onChange={(e) => setForm({ ...form, display_phone: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Access Token permanente {cfg?.access_token_masked && <span className="text-muted-foreground">(actual: {cfg.access_token_masked})</span>}</Label>
                <Input data-testid="wa-access-token" type="password" autoComplete="off" value={form.access_token} className="mt-1"
                  placeholder="EAAG..." onChange={(e) => setForm({ ...form, access_token: e.target.value.trim() })} />
              </div>
              <div>
                <Label className="text-xs">Verify Token {cfg?.verify_token_masked && <span className="text-muted-foreground">(actual: {cfg.verify_token_masked})</span>}</Label>
                <div className="flex gap-2 mt-1">
                  <Input data-testid="wa-verify-token" value={form.verify_token}
                    placeholder="secreto para el webhook"
                    onChange={(e) => setForm({ ...form, verify_token: e.target.value.trim() })} />
                  <Button variant="outline" size="sm" onClick={() => setForm({ ...form, verify_token: randomToken() })} data-testid="wa-generate-verify">
                    <ShieldCheck className="w-4 h-4 mr-1" /> Generar
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">WhatsApp Business Account ID (opcional)</Label>
                <Input data-testid="wa-waba-id" value={form.waba_id} className="mt-1"
                  onChange={(e) => setForm({ ...form, waba_id: e.target.value.trim() })} />
              </div>
            </div>

            {cfg?.connected && (
              <div className="p-3 rounded-xl bg-muted/30 border border-border/60 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">3 · Prueba de envío</p>
                <div className="flex gap-2">
                  <Input data-testid="wa-test-number" value={testTo} placeholder="593991234567"
                    onChange={(e) => setTestTo(e.target.value.replace(/[^\d]/g, ''))} />
                  <Button size="sm" onClick={handleTest} data-testid="wa-test-send" className="gap-1">
                    <Send className="w-4 h-4" /> Enviar
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Formato E.164 sin “+” ni espacios.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {cfg?.connected && (
            <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="wa-delete-config">
              <Trash2 className="w-4 h-4 mr-1" /> Quitar
            </Button>
          )}
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="wa-save-config"
            className="bg-primary">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
